// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Requests LIST page (Stage 6P.1 + 6P.2).
//
// Route: /erp/purchase/requests
//
// Server-side list with debounced q / status / date-range filters,
// AntD Table pagination (25 / 50 / 100), and a ⋯ row-actions menu
// gated by canEditPurchaseRequest / canDeletePurchaseRequest from
// purchaseHelpers.js. Mirrors the compact 22 px row rhythm + slate
// header used by GlassCategoryMasterPage.
//
// Behaviour boundaries:
//   • Backend is the authority for money math, capability checks,
//     and status transitions. Helpers here only HIDE CTAs that the
//     server would reject anyway — they never grant access.
//   • Frontend does NOT touch tenant slug, order anything, or post
//     accounting. List-only surface; CRUD lives elsewhere.
//   • No global CSS edits — all styling scoped under `.pr-shell`
//     via the co-located PurchaseRequestsPage.css.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Dropdown, Input, message, Modal,
  Select, Space, Spin, Table, Tag, Tooltip
} from 'antd';
import {
  DeleteOutlined, EditOutlined, EyeOutlined, FileTextOutlined,
  MoreOutlined, PlusOutlined, ReloadOutlined, SearchOutlined,
  SendOutlined, StopOutlined, SwapOutlined
} from '@ant-design/icons';

import { purchaseRequestsApi } from './purchaseApi';
import {
  canCancelPurchaseRequest,
  canConvertPurchaseRequest,
  canDeletePurchaseRequest,
  canEditPurchaseRequest,
  canSubmitPurchaseRequest,
  PR_PRIORITY_OPTIONS,
  PR_REQUEST_TYPE_OPTIONS,
  PR_STATUS_OPTIONS,
  statusColor,
  statusLabel
} from './purchaseHelpers';
import './PurchaseRequestsPage.css';

const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const NEW_ROUTE       = '/erp/purchase/requests/new';
const DETAIL_ROUTE    = (id) => `/erp/purchase/requests/${encodeURIComponent(id)}`;
const PAGE_SIZE_OPTS  = ['25', '50', '100'];
const DEFAULT_PG_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 350;

const PRIORITY_COLORS = {
  LOW:    'default',
  NORMAL: 'blue',
  HIGH:   'orange',
  URGENT: 'red'
};
const TYPE_COLORS = {
  GENERAL:             'default',
  GLASS:               'cyan',
  HARDWARE:            'geekblue',
  PROFILE:             'purple',
  SERVICE:             'magenta',
  MAINTENANCE:         'gold',
  PROJECT:             'blue',
  STOCK_REPLENISHMENT: 'green'
};

function priorityColor(p) {
  if (!p) return 'default';
  return PRIORITY_COLORS[String(p).toUpperCase()] || 'default';
}
function typeColor(t) {
  if (!t) return 'default';
  return TYPE_COLORS[String(t).toUpperCase()] || 'default';
}
function priorityLabel(p) {
  if (!p) return '';
  const found = PR_PRIORITY_OPTIONS.find((o) => o.value === String(p).toUpperCase());
  return found ? found.label : p;
}
function typeLabel(t) {
  if (!t) return '';
  const found = PR_REQUEST_TYPE_OPTIONS.find((o) => o.value === String(t).toUpperCase());
  return found ? found.label : t;
}

// ─────────────────────────────────────────────────────────────────
// Display helpers — render only; never used for math.
// ─────────────────────────────────────────────────────────────────

const RUPEE_FMT = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

function fmtAmount(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `₹ ${RUPEE_FMT.format(n)}`;
}

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  // dd MMM yyyy — locale-stable for INR ERP audience.
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
}

// Pluck a value tolerant of camel/snake — list rows arrive either way.
function pickAny(row, ...keys) {
  if (!row || typeof row !== 'object') return undefined;
  for (const k of keys) {
    if (k in row && row[k] !== undefined && row[k] !== null) return row[k];
  }
  return undefined;
}

// Normalise an axios-style envelope. The unwrap helper in purchaseApi
// already peels {data:{data:…}} → so we expect either an array OR
// {rows, total, page, pageSize} OR {items, …}.
function shapeListResponse(out) {
  if (Array.isArray(out)) {
    return { rows: out, total: out.length };
  }
  if (!out || typeof out !== 'object') {
    return { rows: [], total: 0 };
  }
  const rows = Array.isArray(out.rows) ? out.rows
             : Array.isArray(out.items) ? out.items
             : Array.isArray(out.data)  ? out.data
             : [];
  const total = Number(out.total ?? out.count ?? rows.length);
  return { rows, total: Number.isFinite(total) ? total : rows.length };
}

// Stable row key — server id when present, otherwise PR no, otherwise index.
function rowKey(row, idx) {
  return pickAny(row, 'id') || pickAny(row, 'prNo', 'pr_no') || `pr-row-${idx}`;
}

// ─────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────

export default function PurchaseRequestsPage() {
  const navigate = useNavigate();

  // Filter state — separate `searchInput` (typing) from debounced
  // `q` (sent to the API). This avoids fetching on every keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [q,            setQ]          = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [dateRange,    setDateRange]   = useState(null); // [dayjs, dayjs] | null

  // Paging + sort state.
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PG_SIZE);
  const [sort,     setSort]     = useState({ field: 'prDate', order: 'descend' });

  // Data + lifecycle state.
  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState('');

  // Debounce search input → q.
  const searchTimerRef = useRef(null);
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setQ(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchInput]);

  // Build query params for the API.
  const buildParams = useCallback(() => {
    const params = { page, pageSize };
    if (q)            params.q      = q;
    if (statusFilter) params.status = statusFilter;
    if (Array.isArray(dateRange) && dateRange.length === 2) {
      const [from, to] = dateRange;
      if (from && typeof from.format === 'function') {
        params.dateFrom = from.format('YYYY-MM-DD');
      }
      if (to && typeof to.format === 'function') {
        params.dateTo = to.format('YYYY-MM-DD');
      }
    }
    if (sort && sort.field) {
      params.sortBy  = sort.field;
      params.sortDir = sort.order === 'ascend' ? 'asc' : 'desc';
    }
    return params;
  }, [page, pageSize, q, statusFilter, dateRange, sort]);

  // Fetch list — abortable so rapid filter changes don't race.
  const abortRef = useRef(null);
  const load = useCallback(async () => {
    if (abortRef.current) abortRef.current.aborted = true;
    const guard = { aborted: false };
    abortRef.current = guard;

    setLoading(true);
    setErr('');
    try {
      const out = await purchaseRequestsApi.list(buildParams());
      if (guard.aborted) return;
      const shaped = shapeListResponse(out);
      setRows(shaped.rows);
      setTotal(shaped.total);
    } catch (e) {
      if (guard.aborted) return;
      const msg = e?.response?.data?.error?.message
        || e?.response?.data?.message
        || e?.message
        || 'Failed to load purchase requests.';
      setErr(msg);
      setRows([]);
      setTotal(0);
    } finally {
      if (!guard.aborted) setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  // ── Row-action handlers ─────────────────────────────────────
  const handleOpen   = useCallback((row) => {
    const id = pickAny(row, 'id');
    if (id) navigate(DETAIL_ROUTE(id));
  }, [navigate]);

  const handleEdit   = useCallback((row) => {
    const id = pickAny(row, 'id');
    if (id) navigate(`${DETAIL_ROUTE(id)}/edit`);
  }, [navigate]);

  const handleSubmit = useCallback(async (row) => {
    const id = pickAny(row, 'id');
    if (!id) return;
    try {
      await purchaseRequestsApi.submit(id);
      message.success('Purchase request submitted for approval.');
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e?.message || 'Failed to submit purchase request.');
    }
  }, [load]);

  const handleConvert = useCallback((row) => {
    const id = pickAny(row, 'id');
    if (id) navigate(`${DETAIL_ROUTE(id)}/convert`);
  }, [navigate]);

  const handleCancel = useCallback((row) => {
    const id = pickAny(row, 'id');
    if (!id) return;
    Modal.confirm({
      title: 'Cancel this purchase request?',
      content: `${pickAny(row, 'prNo', 'pr_no') || 'PR'} will be marked Cancelled.`,
      okText: 'Cancel PR',
      okButtonProps: { danger: true },
      cancelText: 'Keep',
      async onOk() {
        try {
          await purchaseRequestsApi.cancel(id, { reason: 'Cancelled by user' });
          message.success('Purchase request cancelled.');
          load();
        } catch (e) {
          message.error(e?.response?.data?.error?.message
            || e?.message || 'Failed to cancel purchase request.');
        }
      }
    });
  }, [load]);

  const handleDelete = useCallback((row) => {
    const id = pickAny(row, 'id');
    if (!id) return;
    Modal.confirm({
      title: 'Delete this purchase request?',
      content: `${pickAny(row, 'prNo', 'pr_no') || 'PR'} will be removed. This cannot be undone from the list.`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Keep',
      async onOk() {
        try {
          await purchaseRequestsApi.remove(id);
          message.success('Purchase request deleted.');
          load();
        } catch (e) {
          message.error(e?.response?.data?.error?.message
            || e?.message || 'Failed to delete purchase request.');
        }
      }
    });
  }, [load]);

  // ── Columns ─────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      title:    'PR No',
      dataIndex: 'prNo',
      key:       'prNo',
      width:     150,
      sorter:    true,
      sortOrder: sort.field === 'prNo' ? sort.order : null,
      render: (_, row) => {
        const prNo = pickAny(row, 'prNo', 'pr_no');
        const id   = pickAny(row, 'id');
        const label = prNo || '—';
        if (!id) return <span>{label}</span>;
        return (
          <Link className="pr-no-link" to={DETAIL_ROUTE(id)}>{label}</Link>
        );
      }
    },
    {
      title:    'PR Date',
      dataIndex: 'prDate',
      key:       'prDate',
      width:     120,
      sorter:    true,
      sortOrder: sort.field === 'prDate' ? sort.order : null,
      render: (_, row) => fmtDate(pickAny(row, 'prDate', 'pr_date'))
    },
    {
      title:    'Required By',
      dataIndex: 'requiredByDate',
      key:       'requiredByDate',
      width:     130,
      sorter:    true,
      sortOrder: sort.field === 'requiredByDate' ? sort.order : null,
      render: (_, row) => fmtDate(pickAny(row, 'requiredByDate', 'required_by_date'))
    },
    {
      title:    'Type',
      dataIndex: 'requestType',
      key:       'requestType',
      width:     140,
      render: (_, row) => {
        const t = pickAny(row, 'requestType', 'request_type');
        if (!t) return '—';
        return <Tag color={typeColor(t)}>{typeLabel(t)}</Tag>;
      }
    },
    {
      title:    'Priority',
      dataIndex: 'priority',
      key:       'priority',
      width:     100,
      render: (_, row) => {
        const p = pickAny(row, 'priority');
        if (!p) return '—';
        return <Tag color={priorityColor(p)}>{priorityLabel(p)}</Tag>;
      }
    },
    {
      title:    'Requested By',
      dataIndex: 'requestedBy',
      key:       'requestedBy',
      width:     160,
      ellipsis: true,
      render: (_, row) => {
        const name =
             pickAny(row, 'requestedByName', 'requested_by_name')
          || pickAny(row, 'requestedBy',     'requested_by');
        if (!name) return '—';
        return (
          <Tooltip title={name} mouseEnterDelay={0.4}>
            <span>{name}</span>
          </Tooltip>
        );
      }
    },
    {
      title:    'Status',
      dataIndex: 'status',
      key:       'status',
      width:     150,
      render: (_, row) => {
        const s = pickAny(row, 'status');
        if (!s) return '—';
        return <Tag color={statusColor(s)}>{statusLabel(s)}</Tag>;
      }
    },
    {
      title:    'Lines',
      dataIndex: 'lineCount',
      key:       'lineCount',
      width:     80,
      align:     'right',
      render: (_, row) => {
        const c = pickAny(row, 'lineCount', 'line_count', 'linesCount', 'lines_count');
        if (c === undefined || c === null) return '—';
        return Number(c);
      }
    },
    {
      title:    'Estimated Amount',
      dataIndex: 'estimatedAmount',
      key:       'estimatedAmount',
      width:     160,
      align:     'right',
      sorter:    true,
      sortOrder: sort.field === 'estimatedAmount' ? sort.order : null,
      render: (_, row) => {
        const v = pickAny(row,
          'estimatedAmount', 'estimated_amount',
          'totalEstimatedAmount', 'total_estimated_amount');
        return <span className="pr-amount">{fmtAmount(v)}</span>;
      }
    },
    {
      title:     '',
      key:       'actions',
      width:     48,
      fixed:     'right',
      align:     'center',
      render: (_, row) => {
        const items = [];
        const id = pickAny(row, 'id');

        items.push({
          key: 'open',
          icon: <EyeOutlined />,
          label: 'Open',
          disabled: !id,
          onClick: () => handleOpen(row)
        });

        if (canEditPurchaseRequest(row)) {
          items.push({
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit',
            disabled: !id,
            onClick: () => handleEdit(row)
          });
        }
        if (canSubmitPurchaseRequest(row)) {
          items.push({
            key: 'submit',
            icon: <SendOutlined />,
            label: 'Submit for Approval',
            disabled: !id,
            onClick: () => handleSubmit(row)
          });
        }
        if (canConvertPurchaseRequest(row)) {
          items.push({
            key: 'convert',
            icon: <SwapOutlined />,
            label: 'Convert to PO',
            disabled: !id,
            onClick: () => handleConvert(row)
          });
        }
        if (canCancelPurchaseRequest(row)) {
          items.push({
            key: 'cancel',
            icon: <StopOutlined />,
            label: 'Cancel',
            disabled: !id,
            onClick: () => handleCancel(row)
          });
        }
        if (canDeletePurchaseRequest(row)) {
          items.push({ type: 'divider' });
          items.push({
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete',
            danger: true,
            disabled: !id,
            onClick: () => handleDelete(row)
          });
        }

        return (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            menu={{ items }}
          >
            <button
              type="button"
              className="pr-row-actions"
              aria-label="Row actions"
              onClick={(e) => e.preventDefault()}
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        );
      }
    }
  ], [
    sort,
    handleOpen, handleEdit, handleSubmit,
    handleConvert, handleCancel, handleDelete
  ]);

  // ── Table onChange — pagination + sort ──────────────────────
  const handleTableChange = useCallback((pg, _filters, sorter) => {
    if (pg) {
      if (pg.current  && pg.current  !== page)     setPage(pg.current);
      if (pg.pageSize && pg.pageSize !== pageSize) {
        setPageSize(pg.pageSize);
        setPage(1);
      }
    }
    if (sorter && sorter.field) {
      setSort({
        field: sorter.field,
        order: sorter.order || 'descend'
      });
    } else if (sorter && !sorter.order) {
      setSort({ field: 'prDate', order: 'descend' });
    }
  }, [page, pageSize]);

  // ── Empty state ─────────────────────────────────────────────
  const hasFilters = Boolean(q || statusFilter
    || (Array.isArray(dateRange) && dateRange.length === 2));

  const emptyNode = (
    <div className="pr-empty">
      <div className="pr-empty__title">
        {hasFilters
          ? 'No purchase requests match your filters.'
          : 'No purchase requests yet — click New to create your first.'}
      </div>
      {!hasFilters && (
        <div className="pr-empty__cta">
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => navigate(NEW_ROUTE)}
          >
            New Purchase Request
          </Button>
        </div>
      )}
    </div>
  );

  // ── Render ──────────────────────────────────────────────────
  return (
    <div className="pr-shell">
      {/* Header */}
      <div className="pr-headstrip">
        <div className="pr-headstrip__lead">
          <span className="pr-headstrip__icon"><FileTextOutlined /></span>
          <div className="pr-headstrip__text">
            <span className="pr-headstrip__title">Purchase Requests</span>
            <span className="pr-headstrip__subtitle">
              Capture indents, route them for approval, then convert
              approved requests into purchase orders.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="pr-stat-chip">
            <strong>{total}</strong> total
          </span>
        </Space>
      </div>

      {/* Toolbar */}
      <div className="pr-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search PR no, requester, project…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ width: 280 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="Status"
          value={statusFilter || undefined}
          onChange={(v) => { setStatusFilter(v || null); setPage(1); }}
          options={PR_STATUS_OPTIONS}
          style={{ width: 170 }}
        />
        <RangePicker
          size="small"
          allowClear
          value={dateRange || undefined}
          onChange={(v) => { setDateRange(v || null); setPage(1); }}
          placeholder={['PR from', 'PR to']}
          style={{ width: 260 }}
        />
        <div className="pr-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
            loading={loading}
            aria-label="Refresh"
          />
        </Tooltip>
        <Button
          size="small"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate(NEW_ROUTE)}
        >
          New
        </Button>
      </div>

      {/* Error banner (sticky beneath toolbar; does not replace grid) */}
      {err && (
        <div className="mb-1.5">
          <Alert
            type="error"
            showIcon
            message="Could not load purchase requests"
            description={err}
            closable
            onClose={() => setErr('')}
          />
        </div>
      )}

      {/* Grid */}
      <Card className="pr-card" bordered={false} bodyStyle={{ padding: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Spin spinning={loading}>
          <Table
            className="pr-grid"
            size="small"
            rowKey={rowKey}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            pagination={{
              current:          page,
              pageSize,
              total,
              showSizeChanger:  true,
              pageSizeOptions:  PAGE_SIZE_OPTS,
              showTotal:        (t, range) =>
                t === 0 ? '0' : `${range[0]}–${range[1]} of ${t}`,
              size:             'small'
            }}
            onChange={handleTableChange}
            locale={{ emptyText: emptyNode }}
          />
        </Spin>
      </Card>
    </div>
  );
}
